import React from 'react';
import { styled } from '@mui/material/styles';
import {
    Stack,
    Typography,
    Collapse,
    Box,
} from '@mui/material';
import ChevronDown from 'assets/icons/ChevronDown';
import ChevronUp from 'assets/icons/ChevronUp';

import { useQuestionListContext } from 'pages/Faq/context/questionContext';
import CircularLoading from 'components/CircularLoading';
import {Category} from "pages/Faq/types";

const CategoryRow = styled(Box)<{ active?: boolean }>(({ theme, active }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',

    '& .title': {
        fontWeight: 500,
        color: active
            ? theme.palette.blue[100]
            : theme.palette.black[100],
        transition: 'color 300ms',
    },

    '&:hover .title': {
        color: theme.palette.blue[100],
    },
}));

const Subcategory = styled(Typography)<{ active?: boolean }>(
    ({ theme, active }) => ({
        cursor: 'pointer',
        paddingLeft: 16,
        color: active
            ? theme.palette.blue[100]
            : theme.palette.black[100],
        transition: 'color 300ms',

        '&:hover': {
            color: theme.palette.blue[100],
        },
    })
);

const Categories = () => {
    const {
        categoryList,
        loadingCategories,
        selectedCategory,
        setSelectedCategory,
        selectedSubCategory,
        setSelectedSubCategory
    } = useQuestionListContext();

    if (loadingCategories) return <CircularLoading />;

    const handleCategoryClick = (category : Category) => {
        if (selectedCategory?.id === category.id) {
            setSelectedCategory(null);
            setSelectedSubCategory(null);
        } else {
            setSelectedCategory(category);
            setSelectedSubCategory(null);
        }
    };

    const handleSubcategoryClick = (e: React.MouseEvent, subId: string) => {
        e.stopPropagation();
        if (selectedSubCategory === subId) {
            setSelectedSubCategory(null);
        } else {
            setSelectedSubCategory(subId);
        }
    };

    return (
        <Stack spacing={3}>
            {categoryList?.map(category => {
                const hasSubcategories = category.subcategories && category.subcategories.length > 0;

                const isExpanded =
                    selectedCategory?.id === category.id ||
                    category.subcategories?.some(
                        sub => sub.id === selectedSubCategory
                    );

                const isActiveCategory = selectedCategory?.id === category.id;

                return (
                    <Stack key={category.id} spacing={1}>
                        <CategoryRow
                            active={isActiveCategory}
                            onClick={() => handleCategoryClick(category)}
                        >
                            <Typography variant="h5" className="title">
                                {category.title}
                            </Typography>

                            {hasSubcategories && (isExpanded ? <ChevronUp sx={{ color: 'blue.100' }} /> : <ChevronDown />)}
                        </CategoryRow>

                        {hasSubcategories && (
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Stack spacing={1}>
                                    {category.subcategories?.map(sub => (
                                        <Subcategory
                                            key={sub.id}
                                            variant="body1"
                                            active={selectedSubCategory === sub.id}
                                            onClick={(e) => handleSubcategoryClick(e, sub.id)}
                                        >
                                            {sub.name}
                                        </Subcategory>
                                    ))}
                                </Stack>
                            </Collapse>
                        )}
                    </Stack>
                );
            })}
        </Stack>
    );
};

export default Categories;